import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router';
import {useDebounceFn, useMemoizedFn} from 'ahooks';
import global from '@/utils/global';
import { isRecordReview } from '@/utils/recordReview';
import Bus from '@/utils/bus';
import AIContant from '@/utils/aiContant';
import { IClearHighlightPayload, IDeleteNotePayload, ISaveAnswerHandle, IUpdatePanel, IhightLightItem } from '@/utils/ctype';
import { login } from '@/utils/login';
import {useAppSelector} from '@/stores/index';
import { useAppDispatch } from '@/stores/index';
import {getReadingAsync, getAnswerListAsync, setPanelDataByIndex, setFocusIndex, saveAnswer,
  saveHighlightData, updateTimeState, updateRecordId, saveAnswerListAsync,
  submitAnswerAsync, toggleShowNotePanel, clearHightLightInfo, updatePanelInfo, setShowDragFiled,
  getReadingFeedbackAsync, deleteNote, resetExamLoadState} from '@/stores/slice/reading';
import { getStudentInfoAsync } from '@/stores/slice/user';
import { getExamInfoAsync } from '@/stores/slice/exam';
import NotePanel from '../../components/NotePanel';
import Bottom from '../../components/Bottom';
import Main from '../../components/Main';
import { AIPanel } from '../../components/AIPanel';
import Loading from '../../components/Loading';
import ExamLoadingNotice from '../../components/ExamLoadingNotice';
import { useExamLoading } from '../../useExamLoading';
import { useExamSubmission } from '../../useExamSubmission';
import MainContentAI from './MainContentAI';
import MainContent from './MainContent';
import style from './index.module.less';

export default function MockListening() {

  global.subject = 'read';

  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAI = location.pathname.indexOf('mockreadingai') > -1;
  const {showNotePanel, answerAll, answerMap, limits, currentFocus, pageDataIndex, loadAnswered, pageDataCache, highLightInfo, surplus_time, draftSyncedAt, recordId, showDragField, resultMap} = useAppSelector(state => state.reading);
  const currentPapeId = useAppSelector(state => state.reading.pageData?.question.paper_id);
  const status = useAppSelector(state => state.exam.info?.reading.status);

  const isLogin = useAppSelector(state => state.user.userPermission.isLogin);
  const inited = useAppSelector(state => state.user.inited);
  const isVip = useAppSelector(state => state.user.userPermission.isVip);
  const displayAnswerMap = useAppSelector(state => state.listening.displayAnswerMap);
  const routeRecordId = Number(params.recordId);
  const readOnly = isRecordReview(location.search);

  useEffect(() => {
    if (inited && !isLogin) {
      login();
    }
  }, [isLogin, inited]);

  useEffect(() => {
    if (readOnly) return;
    console.log('页面前置检查', 'isAI:', isAI, 'status:', status);
    if (status === 3) {
      // 当前不应该打开页面
      console.log('跳回开始页');
      navigate(`/mockstart/${recordId}`);
      return;
    }

    if (isAI && (status === 1 || status === 2)) {
      console.log('跳回做题页');
      navigate(`/mockreading/${recordId}`);
      return;
    }
    if (!isAI && status === 4) {
      console.log('跳回答案页');
      navigate(`/mockreadingai/${recordId}`);
      return;
    }
  }, [isAI, status, readOnly]);


  useEffect(() => {
    dispatch(updateRecordId(routeRecordId));
    dispatch(resetExamLoadState());
  }, [dispatch, routeRecordId]);

  const loadExam = useCallback(async (signal: AbortSignal) => {
    const answerRequest = dispatch(getAnswerListAsync({record_id: routeRecordId, signal}));
    const contentRequest = dispatch(getReadingAsync({record_id: routeRecordId, signal}));
    try {
      await Promise.all([answerRequest.unwrap(), contentRequest.unwrap()]);
    } catch (error) {
      answerRequest.abort();
      contentRequest.abort();
      await Promise.allSettled([answerRequest, contentRequest]);
      throw error;
    }
  }, [dispatch, routeRecordId]);
  const {ready: draftLoaded, status: loadingStatus, retry: retryLoad} = useExamLoading({recordId: routeRecordId, load: loadExam});
  const {showLoading, submitHandle, isFinalizing} = useExamSubmission({
    recordId: routeRecordId,
    course: 'reading',
    ready: draftLoaded && !isAI && !readOnly,
    expired: !isAI && !readOnly && surplus_time <= 0,
    save: signal => dispatch(saveAnswerListAsync({recordId: routeRecordId, signal})).unwrap(),
    submit: signal => dispatch(submitAnswerAsync({recordId: routeRecordId, signal})).unwrap(),
  });

  useEffect(() => {
    if (!isAI && !readOnly && draftLoaded) {
      const timer = setInterval(() => {
        if (!isFinalizing()) dispatch(saveAnswerListAsync());
      }, 10000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [draftLoaded, isAI, readOnly, dispatch]);

  const { run, cancel, flush } = useDebounceFn(() => {
    if (!readOnly && draftLoaded && !isFinalizing()) {
      dispatch(saveAnswerListAsync());
    }
  }, { wait: 1500, });
  useEffect(() => cancel, [cancel, routeRecordId]);
  
  useEffect(() => {
    dispatch(getExamInfoAsync({record_id: routeRecordId}));
    if (isAI) {
      dispatch(getReadingFeedbackAsync({record_id: routeRecordId}));
      dispatch(getStudentInfoAsync());
    }
  }, [dispatch, isAI, routeRecordId]);

  const setPanelHandle = (index: number) => {
    dispatch(setPanelDataByIndex({index}));
    if (isAI){
      Bus.publish(AIContant.AUDIO_STOP, undefined);
    }
  };

  const setFocusIndexHandle = (index: number) => {
    dispatch(setFocusIndex({index}));
  };

  const saveAnswerHandle: ISaveAnswerHandle = (index, value) => {
    if (readOnly || !draftLoaded || isFinalizing()) {
      return;
    }
    if (Array.isArray(index) && Array.isArray(value)) {
      dispatch(saveAnswer({index, value}));
    } else if (!Array.isArray(index) && !Array.isArray(value)){
      dispatch(saveAnswer({index, value}));
    }
    run();
  };

  const saveHighlightDataHandle =(params: {show: boolean;wrapUid: string;} & IhightLightItem) => {
    if (readOnly || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(saveHighlightData(params));
    run();
  };

  const updateTimeHandle = (value: number) => {
    if (!readOnly) dispatch(updateTimeState(value));
  };

  const noteHandle = () => {
    dispatch(toggleShowNotePanel());
  };

  const clearDialogHandle= (payload?: IClearHighlightPayload) => {
    if (readOnly || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(clearHightLightInfo(payload));
    run();
  };

  const updateDialogInfo = (info:IUpdatePanel) => {
    if (readOnly || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(updatePanelInfo(info));
    run();
    flush();
  };

  const deleteNoteHandle = (payload: IDeleteNotePayload) => {
    if (readOnly || !draftLoaded || isFinalizing()) {
      return;
    }
    dispatch(deleteNote(payload));
    run();
  };

  const aiContent = useMemo(() => {
    return <AIPanel paper_id={currentPapeId} course="r" />;
  }, [currentPapeId]);

  const setShowDragFieldHandle = (val: boolean) => {
    dispatch(setShowDragFiled(val));
  };


  return (
    <div className={style.viewBox}>
      <Main course="r" pageData={pageDataCache[pageDataIndex]} highLightObj={highLightInfo[pageDataIndex]}
        time={surplus_time} isAI={isAI || readOnly} readOnly={readOnly} saveHighlightDataHandle={saveHighlightDataHandle}
        draftSyncedAt={draftSyncedAt}
        loadAnswered={draftLoaded && loadAnswered}
        updateTimeHandle={updateTimeHandle} submitHandle={submitHandle} clearDialogHandle={clearDialogHandle}
        noteHandle={noteHandle} showNotePanel={showNotePanel} aiContent={aiContent}
        notePanel={<NotePanel showNotePanel={showNotePanel} highLightInfo={highLightInfo} noteHandle={noteHandle} readOnly={readOnly}
          updateNoteHandle={updateDialogInfo} deleteNoteHandle={deleteNoteHandle}></NotePanel>}>
        {
          pageDataCache.map((pageDataItem, index: number) => {
            return (
              pageDataItem ? isAI ? 
                <MainContentAI key={index} question={pageDataItem.question.question} currentFocus={currentFocus}
                  saveAnswerHandle={saveAnswerHandle} answerMap={answerMap} pageData={pageDataItem}
                  displayAnswerMap={displayAnswerMap}
                  loadAnswered={draftLoaded && loadAnswered} isAI={isAI || readOnly} index={index}
                  boxStyle={{display: pageDataIndex === index ? '' : 'none'}}
                  setShowDragFieldHandle={setShowDragFieldHandle} showDragField={showDragField} feedback={resultMap}></MainContentAI> : 
                <MainContent key={index} question={pageDataItem.question.question} currentFocus={currentFocus}
                  displayAnswerMap={displayAnswerMap}
                  saveAnswerHandle={saveAnswerHandle} answerMap={answerMap} pageData={pageDataItem}
                  loadAnswered={draftLoaded && loadAnswered} isAI={isAI || readOnly} index={index}
                  boxStyle={{display: pageDataIndex === index ? '' : 'none'}}
                  setShowDragFieldHandle={setShowDragFieldHandle} showDragField={showDragField}
                  setFocusIndexHandle={setFocusIndexHandle}></MainContent>
                : null
            );
          })
        }
      </Main>
      <Bottom limits={limits} answerAll={answerAll} currentFocus={currentFocus} setPanelHandle={setPanelHandle} pageDataIndex={pageDataIndex} setFocusIndexHandle={setFocusIndexHandle} showNotePanel={showNotePanel}></Bottom>
      <ExamLoadingNotice status={loadingStatus} onRetry={retryLoad} />
      <Loading show={showLoading}></Loading>
    </div>
  );
}
